import { useEffect, useRef, useState } from 'react'
import { formatBytes, processImage, type ProcessedImage } from '../lib/image'
import { useToast } from '../state/toast'

export interface AttachedPhoto extends ProcessedImage {
  id: string
  previewUrl: string
  originalName: string
}

interface PhotoPickerProps {
  photos: AttachedPhoto[]
  onChange: (photos: AttachedPhoto[]) => void
  max?: number
  maxDimension: number
  quality: number
}

/**
 * Camera-first photo input.
 *
 * `capture="environment"` opens the rear camera straight away, which is what
 * somebody standing in front of the work wants. Files are resized on the phone
 * before they are held in memory — a six-photo update stays under a megabyte.
 */
export function PhotoPicker({
  photos, onChange, max = 6, maxDimension, quality,
}: PhotoPickerProps) {
  const toast = useToast()
  const cameraRef = useRef<HTMLInputElement>(null)
  const libraryRef = useRef<HTMLInputElement>(null)
  const [working, setWorking] = useState(false)

  // Object URLs leak if they outlive the component.
  useEffect(() => () => {
    for (const photo of photos) URL.revokeObjectURL(photo.previewUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList?.length) return
    const room = max - photos.length
    if (room <= 0) {
      toast.warn(`That is the limit of ${max} photos`)
      return
    }

    setWorking(true)
    const added: AttachedPhoto[] = []
    for (const file of Array.from(fileList).slice(0, room)) {
      try {
        const processed = await processImage(file, { maxDimension, quality })
        added.push({
          ...processed,
          id: crypto.randomUUID(),
          previewUrl: URL.createObjectURL(processed.thumb),
          originalName: file.name || 'photo.jpg',
        })
      } catch {
        toast.error(`Could not read ${file.name || 'that photo'}`)
      }
    }
    setWorking(false)
    if (added.length) onChange([...photos, ...added])
  }

  const remove = (id: string) => {
    const target = photos.find((p) => p.id === id)
    if (target) URL.revokeObjectURL(target.previewUrl)
    onChange(photos.filter((p) => p.id !== id))
  }

  const totalBytes = photos.reduce((n, p) => n + p.full.size, 0)

  return (
    <div>
      <div className="flex gap-2">
        <button
          type="button"
          className="btn btn-secondary flex-1"
          onClick={() => cameraRef.current?.click()}
          disabled={working || photos.length >= max}
        >
          {working ? 'Preparing…' : 'Take photo'}
        </button>
        <button
          type="button"
          className="btn btn-secondary flex-1"
          onClick={() => libraryRef.current?.click()}
          disabled={working || photos.length >= max}
        >
          Choose photo
        </button>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          void handleFiles(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={(e) => {
          void handleFiles(e.target.files)
          e.target.value = ''
        }}
      />

      {photos.length > 0 && (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <div key={photo.id} className="relative">
                <img
                  src={photo.previewUrl}
                  alt={photo.originalName}
                  className="aspect-square w-full rounded border border-line object-cover"
                />
                <button
                  type="button"
                  onClick={() => remove(photo.id)}
                  className="absolute -top-2 -right-2 flex size-7 items-center justify-center rounded-full bg-graphite text-lg leading-none text-concrete"
                  aria-label={`Remove ${photo.originalName}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 font-mono text-[10px] tracking-wider text-graphite-2 uppercase">
            {photos.length} of {max} · {formatBytes(totalBytes)} to upload
          </p>
        </>
      )}
    </div>
  )
}
